import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarDays, Trophy, ArrowRight, MapPin, Radio, Megaphone, ClipboardList } from "lucide-react";
import { api } from "@/lib/api";
import { IMAGES } from "@/lib/images";
import Countdown from "@/components/Countdown";
import RegisterDialog from "@/components/RegisterDialog";
import TeamCrest from "@/components/TeamCrest";

const fadeUp = {
    hidden: { opacity: 0, y: 28 },
    show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.12, ease: "easeOut" } }),
};

function ArenaFX() {
    const embers = useMemo(
        () =>
            Array.from({ length: 24 }, (_, i) => ({
                left: `${(i * 43 + 7) % 100}%`,
                size: 2 + ((i * 5) % 4),
                delay: `${((i * 0.77) % 8).toFixed(2)}s`,
                duration: `${(7 + ((i * 1.61) % 7)).toFixed(2)}s`,
                sway: `${((i * 53) % 90) - 45}px`,
                amber: i % 3 !== 0,
            })),
        []
    );
    return (
        <div className="absolute inset-0 overflow-hidden pointer-events-none" data-testid="arena-fx">
            <div className="arena-beam arena-beam-a" />
            <div className="arena-beam arena-beam-b" />
            <div className="arena-raysweep" />
            {embers.map((e, i) => (
                <span
                    key={i}
                    className="home-ember absolute rounded-full"
                    style={{
                        left: e.left,
                        bottom: "-8px",
                        width: e.size,
                        height: e.size,
                        background: e.amber ? "#FFB800" : "#FF5500",
                        boxShadow: `0 0 ${e.size * 3}px ${e.amber ? "#FFB800" : "#FF5500"}`,
                        animationDelay: e.delay,
                        animationDuration: e.duration,
                        "--sway": e.sway,
                    }}
                />
            ))}
            <style>{`
                .home-ember { animation-name: home-ember-rise; animation-timing-function: linear; animation-iteration-count: infinite; opacity: 0; }
                @keyframes home-ember-rise {
                    0% { transform: translateY(0) translateX(0); opacity: 0; }
                    10% { opacity: .9; }
                    100% { transform: translateY(-105vh) translateX(var(--sway)); opacity: 0; }
                }
                .arena-beam { position: absolute; top: -25%; width: 340px; height: 150%; filter: blur(28px); opacity: .5; transform-origin: top center;
                    clip-path: polygon(42% 0, 58% 0, 100% 100%, 0 100%); }
                .arena-beam-a { left: 6%; background: linear-gradient(to bottom, rgba(255,184,0,.26), transparent 65%); animation: arena-beam-sway-a 11s ease-in-out infinite alternate; }
                .arena-beam-b { right: 4%; background: linear-gradient(to bottom, rgba(255,85,0,.22), transparent 60%); animation: arena-beam-sway-b 14s ease-in-out infinite alternate; }
                @keyframes arena-beam-sway-a { from { transform: rotate(-14deg); } to { transform: rotate(10deg); } }
                @keyframes arena-beam-sway-b { from { transform: rotate(12deg); } to { transform: rotate(-10deg); } }
                .arena-raysweep { position: absolute; top: 0; bottom: 0; width: 38%; left: -45%;
                    background: linear-gradient(100deg, transparent, rgba(255,200,90,.07), transparent);
                    animation: arena-ray 9s linear infinite; }
                @keyframes arena-ray { to { left: 115%; } }
            `}</style>
        </div>
    );
}

export default function Home() {
    const [summary, setSummary] = useState(null);
    const [sponsors, setSponsors] = useState([]);
    const [registerOpen, setRegisterOpen] = useState(false);
    const announcement = summary?.announcement;
    const sponsorHalf = sponsors.length
        ? Array.from({ length: Math.max(1, Math.ceil(8 / sponsors.length)) }, () => sponsors).flat()
        : [];

    useEffect(() => {
        const load = () => {
            api.get("/league/summary").then((r) => setSummary(r.data)).catch(() => {});
            api.get("/sponsors").then((r) => setSponsors(r.data || [])).catch(() => {});
        };
        load();
        const t = setInterval(load, 8000);
        return () => clearInterval(t);
    }, []);

    const next = summary?.next_game;

    return (
        <div data-testid="home-page">
            {announcement?.active && announcement.text && (
                <div
                    data-testid="announcement-banner"
                    className="relative border-b border-[#FFB800]/30 bg-gradient-to-r from-[#FFB800]/10 via-[#FF5500]/10 to-[#FFB800]/10"
                >
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
                        <Megaphone className="w-4 h-4 text-[#FFB800] shrink-0" />
                        <span className="text-[10px] uppercase tracking-[0.25em] text-[#FFB800] font-semibold shrink-0 hidden sm:block">
                            League Office
                        </span>
                        <p className="text-sm text-slate-200 leading-snug min-w-0 flex-1 break-words">{announcement.text}</p>
                    </div>
                </div>
            )}
            <section data-testid="hero-section" className="relative min-h-[92vh] flex items-center overflow-hidden">
                <img
                    src={IMAGES.heroDunk}
                    alt="Basketball player dunking under arena lights"
                    className="absolute inset-0 w-full h-full object-cover opacity-35"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#08080C]/70 via-[#08080C]/30 to-[#08080C]" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#08080C]/80 via-transparent to-[#08080C]/60" />
                <div className="spotlight spotlight-ember top-[-10%] left-[-10%]" />
                <div className="spotlight spotlight-gold bottom-[-20%] right-[-5%]" />
                <ArenaFX />

                <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-24 w-full">
                    <motion.p
                        variants={fadeUp}
                        initial="hidden"
                        animate="show"
                        custom={0}
                        className="text-xs font-semibold uppercase tracking-[0.3em] text-[#FFB800] mb-6"
                    >
                        YKP Basketball League • Season {new Date().getFullYear()}-{new Date().getFullYear() + 1}
                    </motion.p>
                    <motion.h1
                        variants={fadeUp}
                        initial="hidden"
                        animate="show"
                        custom={1}
                        className="font-display font-black uppercase leading-[0.85] tracking-tight"
                    >
                        <span className="block text-stroke-ember text-7xl sm:text-9xl lg:text-[12rem]">YKPBL</span>
                        <span className="block text-white text-4xl sm:text-5xl lg:text-6xl mt-4">
                            Where the city <span className="text-[#FF5500]">comes to play</span>
                        </span>
                    </motion.h1>
                    <motion.p
                        variants={fadeUp}
                        initial="hidden"
                        animate="show"
                        custom={2}
                        className="mt-6 max-w-xl text-slate-300 text-sm sm:text-base leading-relaxed"
                    >
                        Under the lights. Above the rim. Follow every roster, every fixture and every rung of the
                        standings ladder — live from the heart of the league.
                    </motion.p>
                    <motion.p
                        variants={fadeUp}
                        initial="hidden"
                        animate="show"
                        custom={3}
                        className="mt-8 text-sm text-slate-400"
                    >
                        Don't have a team yet?{" "}
                        <button
                            data-testid="hero-register-link"
                            onClick={() => setRegisterOpen(true)}
                            className="text-[#FFB800] font-display font-bold uppercase tracking-widest text-xs hover:text-[#FF5500] transition-colors duration-200 underline underline-offset-4 decoration-[#FFB800]/40"
                        >
                            Register now
                        </button>
                    </motion.p>
                    <motion.div
                        variants={fadeUp}
                        initial="hidden"
                        animate="show"
                        custom={3}
                        className="mt-6 flex flex-wrap items-center gap-4"
                    >
                        <Link
                            to="/schedule"
                            data-testid="hero-cta-schedule"
                            className="animate-pulse-glow flex items-center gap-2 bg-[#FF5500] hover:bg-[#E04800] text-white font-display font-bold uppercase tracking-widest px-8 py-4 rounded-full transition-colors duration-200"
                        >
                            <CalendarDays className="w-5 h-5" /> View Schedule
                        </Link>
                        <Link
                            to="/standings"
                            data-testid="hero-cta-standings"
                            className="flex items-center gap-2 border border-slate-600 hover:border-[#FFB800] hover:text-[#FFB800] text-slate-200 font-display font-bold uppercase tracking-widest px-8 py-4 rounded-full transition-colors duration-200"
                        >
                            <Trophy className="w-5 h-5" /> Standings
                        </Link>
                        <button
                            data-testid="hero-cta-register"
                            onClick={() => setRegisterOpen(true)}
                            className="flex items-center gap-2 border border-[#FFB800] text-[#FFB800] hover:bg-[#FFB800] hover:text-black font-display font-bold uppercase tracking-widest px-8 py-4 rounded-full transition-colors duration-200"
                        >
                            <ClipboardList className="w-5 h-5" /> Register to Play
                        </button>
                    </motion.div>

                    {summary?.live_games?.length > 0 && (
                        <motion.div
                            variants={fadeUp}
                            initial="hidden"
                            animate="show"
                            custom={4}
                            data-testid="live-now-card"
                            className="mt-16 max-w-2xl bg-[#12121A]/80 border border-[#EF4444]/50 rounded-2xl p-5 sm:p-6 backdrop-blur-md"
                        >
                            <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-[#EF4444] mb-4">
                                <span className="w-2 h-2 rounded-full bg-[#EF4444] animate-pulse" /> Live now
                            </p>
                            {summary.live_games.map((g) => (
                                <div key={g.id} className="flex flex-wrap items-center gap-5 justify-between">
                                    <div className="flex items-center gap-4">
                                        <TeamCrest team={g.home_team} size="md" />
                                        <span className="font-stat font-bold text-3xl">
                                            <span className="text-[#EF4444]">{g.home_score ?? 0}</span>
                                            <span className="text-slate-600 mx-1.5">:</span>
                                            <span className="text-[#EF4444]">{g.away_score ?? 0}</span>
                                        </span>
                                        <TeamCrest team={g.away_team} size="md" />
                                        <span className="text-xs text-slate-400 font-stat">
                                            {g.quarter} {g.clock}
                                        </span>
                                    </div>
                                    <Link
                                        to="/live"
                                        data-testid={`hero-watch-live-${g.id}`}
                                        className="flex items-center gap-2 bg-[#EF4444] hover:bg-[#DC2626] text-white font-display font-bold uppercase tracking-widest text-xs px-5 py-2.5 rounded-full transition-colors duration-200"
                                    >
                                        <Radio className="w-4 h-4" /> Watch Live
                                    </Link>
                                </div>
                            ))}
                        </motion.div>
                    )}

                    {next && (
                        <motion.div
                            variants={fadeUp}
                            initial="hidden"
                            animate="show"
                            custom={4}
                            data-testid="next-game-card"
                            className="mt-16 max-w-2xl bg-[#12121A]/80 border border-[#262638] rounded-2xl p-5 sm:p-6 backdrop-blur-md"
                        >
                            <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#FF5500] mb-4">
                                Next tip-off
                            </p>
                            <div className="flex flex-col sm:flex-row sm:items-center gap-6 justify-between">
                                <div className="flex items-center gap-5">
                                    <div className="flex flex-col items-center gap-2">
                                        <TeamCrest team={next.home_team} size="lg" testid="next-game-home-crest" />
                                        <span className="font-display font-bold uppercase text-xs text-slate-300">
                                            {next.home_team?.short_name}
                                        </span>
                                    </div>
                                    <span className="font-display font-black text-3xl text-stroke-faint px-1">VS</span>
                                    <div className="flex flex-col items-center gap-2">
                                        <TeamCrest team={next.away_team} size="lg" testid="next-game-away-crest" />
                                        <span className="font-display font-bold uppercase text-xs text-slate-300">
                                            {next.away_team?.short_name}
                                        </span>
                                    </div>
                                </div>
                                <div className="flex flex-col gap-3">
                                    <Countdown target={next.date} testid="countdown-timer" />
                                    <p className="flex items-center gap-1.5 text-xs text-slate-400">
                                        <MapPin className="w-3.5 h-3.5 text-[#FFB800]" />
                                        {next.venue} • Week {next.week ?? "—"}
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </div>
            </section>

            {summary && (
                <section className="border-y border-[#262638] bg-[#0C0C13] overflow-hidden py-4" data-testid="results-marquee">
                    <div className="marquee-track gap-10">
                        {[...summary.recent_results, ...summary.recent_results].map((g, i) => (
                            <div key={`${g.id}-${i}`} className="flex items-center gap-3 shrink-0">
                                <span className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500">
                                    Final
                                </span>
                                <span className="font-display font-bold uppercase text-slate-200">
                                    {g.home_team?.short_name}
                                </span>
                                <span className="font-stat font-bold text-[#FF5500]">
                                    {g.home_score} — {g.away_score}
                                </span>
                                <span className="font-display font-bold uppercase text-slate-200">
                                    {g.away_team?.short_name}
                                </span>
                                <span className="w-1.5 h-1.5 rounded-full bg-[#FFB800]" />
                            </div>
                        ))}
                    </div>
                </section>
            )}

            <section className="max-w-7xl mx-auto px-4 sm:px-6 py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: "-80px" }}
                    className="relative rounded-3xl overflow-hidden border border-[#262638]"
                >
                    <img src={IMAGES.arena} alt="Arena under the lights" className="w-full h-[420px] object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#08080C] via-transparent to-transparent" />
                    <div className="absolute bottom-6 left-6">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#FFB800]">The Arena</p>
                        <p className="font-display font-black uppercase text-3xl text-white">Every Saturday Night</p>
                    </div>
                </motion.div>
                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: "-80px" }}
                    custom={1}
                >
                    <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#FF5500] mb-4">The League</p>
                    <h2 data-testid="home-squad-count" className="font-display font-black uppercase text-3xl sm:text-4xl text-white leading-tight">
                        <span className="text-[#FF5500]">{summary?.team_count ?? "—"}</span>{" "}
                        {summary?.team_count === 1 ? "squad" : "squads"}. One crown.{" "}
                        <span className="text-[#FFB800]">Zero mercy.</span>
                    </h2>
                    <p className="mt-5 text-slate-300 text-sm sm:text-base leading-relaxed max-w-lg">
                        YKPBL is the city's proving ground — packed gyms, buzzer beaters and rivalries built one
                        hard-fought possession at a time. Track every team, every player and every result as the
                        season unfolds.
                    </p>
                    <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
                        {[
                            { label: "Teams", value: summary?.team_count, testid: "stat-teams" },
                            { label: "Players", value: summary?.player_count, testid: "stat-players" },
                            { label: "Games Played", value: summary?.games_played, testid: "stat-games" },
                        ].map((s, i) => (
                            <div
                                key={s.label}
                                data-testid={s.testid}
                                className="card-arena rounded-2xl p-4 text-center animate-float-slow"
                                style={{ animationDelay: `${i * 0.8}s` }}
                            >
                                <div className="font-stat font-bold text-3xl text-[#FF5500]">{s.value ?? "—"}</div>
                                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mt-1">
                                    {s.label}
                                </div>
                            </div>
                        ))}
                    </div>
                    <Link
                        to="/teams"
                        data-testid="home-cta-teams"
                        className="mt-8 inline-flex items-center gap-2 text-[#FF5500] hover:text-[#FFB800] font-display font-bold uppercase tracking-widest transition-colors duration-200"
                    >
                        Meet the squads <ArrowRight className="w-4 h-4" />
                    </Link>
                </motion.div>
            </section>
            {sponsors.length > 0 && (
                <section
                    className="border-t border-[#262638] bg-[#0C0C13] overflow-hidden py-6"
                    data-testid="sponsors-marquee"
                >
                    <p className="text-center text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-500 mb-4">
                        Proudly supported by
                    </p>
                    <div className="marquee-fade overflow-hidden">
                        <div className="marquee-track marquee-track-fast items-center">
                        {[...sponsorHalf, ...sponsorHalf].map((s, i) => {
                            const logo = s.logo_url ? (
                                <img
                                    src={s.logo_url}
                                    alt={s.name}
                                    className="h-10 w-auto max-w-[140px] object-contain opacity-70 hover:opacity-100 transition-opacity duration-200"
                                />
                            ) : (
                                <span className="font-display font-bold uppercase text-slate-300 text-sm tracking-widest">
                                    {s.name}
                                </span>
                            );
                            return (
                                <div
                                    key={`${s.id}-${i}`}
                                    data-testid={i < sponsors.length ? `sponsor-logo-${s.id}` : undefined}
                                    className="card-arena rounded-2xl px-7 py-4 mx-5 flex items-center justify-center shrink-0"
                                >
                                    {s.link ? (
                                        <a href={s.link} target="_blank" rel="noreferrer">
                                            {logo}
                                        </a>
                                    ) : (
                                        logo
                                    )}
                                </div>
                            );
                        })}
                        </div>
                    </div>
                </section>
            )}
            <RegisterDialog open={registerOpen} onOpenChange={setRegisterOpen} />
        </div>
    );
}
